import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { Loader, ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { API_CONFIG } from '../config/api';
import { configureLivenessAmplify } from '../config/livenessAmplify';
import { BIOMETRIC_GUIDANCE } from '../constants/biometricCapture';

/**
 * AWS Rekognition Face Liveness capture (short guided video selfie).
 * Calls backend to create session, then runs Amplify FaceLivenessDetector.
 *
 * @param {{ onComplete: (sessionId: string) => void, onError?: (message: string) => void, apiBase?: string }} props
 */
export default function BiometricLivenessCapture({ onComplete, onError, apiBase }) {
  const baseUrl = apiBase || API_CONFIG.BASE_URL;
  const [sessionId, setSessionId] = useState(null);
  const [region, setRegion] = useState('us-west-2');
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState(null);

  const reportError = useCallback(
    (message) => {
      setInitError(message);
      if (onError) onError(message);
    },
    [onError]
  );

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const configRes = await axios.get(
          `${baseUrl}/biometrics/liveness/config`,
          { timeout: 30000 }
        );
        const {
          identityPoolId,
          region: apiRegion,
          livenessRequired,
        } = configRes.data || {};

        if (!identityPoolId && livenessRequired) {
          throw new Error(
            'Face liveness is not configured on the server (missing identity pool).'
          );
        }

        if (identityPoolId) {
          configureLivenessAmplify(identityPoolId, apiRegion || 'us-west-2');
        }

        const sessionRes = await axios.post(
          `${baseUrl}/biometrics/liveness/session`,
          {},
          { timeout: 30000 }
        );

        if (cancelled) return;

        const sid = sessionRes.data?.sessionId;
        if (!sid) {
          throw new Error('Could not start face liveness session.');
        }

        setSessionId(sid);
        setRegion(sessionRes.data?.region || apiRegion || 'us-west-2');
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const msg =
          err.response?.data?.message ||
          err.message ||
          'Failed to start face verification.';
        reportError(msg);
        setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [reportError]);

  const handleAnalysisComplete = useCallback(async () => {
    if (sessionId) {
      onComplete(sessionId);
    }
  }, [sessionId, onComplete]);

  const handleLivenessError = useCallback(
    (error) => {
      const msg =
        error?.message ||
        error?.toString?.() ||
        'Face liveness check failed. Please try again.';
      reportError(msg);
    },
    [reportError]
  );

  if (initError) {
    return <p className="biometric-capture-status biometric-capture-error">{initError}</p>;
  }

  if (loading || !sessionId) {
    return (
      <div className="biometric-liveness-loading">
        <Loader />
        <p className="biometric-capture-status">Preparing live face verification…</p>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <p className="biometric-capture-status">{BIOMETRIC_GUIDANCE.positioning}</p>
      <p className="biometric-tips">
        Follow the on-screen oval. Keep your face visible for a few seconds.
      </p>
      <div className="biometric-liveness-detector">
        <FaceLivenessDetector
          sessionId={sessionId}
          region={region}
          onAnalysisComplete={handleAnalysisComplete}
          onError={handleLivenessError}
        />
      </div>
    </ThemeProvider>
  );
}
